import React, { createContext, useContext, useState, ReactNode } from 'react';
import { User } from '../data/mockData';
import axios from 'axios';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get backend URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.workflow.alokpandey.org';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      console.log('Attempting login to:', `${API_BASE_URL}/api/auth/login`);

      // Call Cognito login endpoint with timeout
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      }, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Login response:', response.data);

      if (response.data.success && response.data.data.accessToken) {
        const { accessToken, idToken } = response.data.data;

        // Store tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);

        console.log('Fetching user info...');

        // Get user info
        const userInfoResponse = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          },
          timeout: 10000
        });

        console.log('User info response:', userInfoResponse.data);

        if (userInfoResponse.data.success) {
          const userData = userInfoResponse.data.data;
          const userRole = userData.role || 'viewer'; // Get role from backend

          // Map role to permissions
          const rolePermissions: Record<string, string[]> = {
            admin: ['all'],
            workflow_creator: ['workflow:view', 'workflow:create', 'workflow:edit', 'workflow:delete', 'execution:view', 'template:view'],
            workflow_executor: ['workflow:view', 'workflow:execute', 'execution:view', 'execution:approve', 'template:view', 'template:execute'],
            viewer: ['workflow:view', 'execution:view', 'template:view']
          };

          // Create user object compatible with existing UI
          const userObj: User = {
            id: userData.username,
            username: userData.attributes.email,
            email: userData.attributes.email,
            role: userRole,
            permissions: rolePermissions[userRole] || rolePermissions.viewer
          };

          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
          setIsLoading(false);
          console.log('Login successful! Role:', userRole);
          return true;
        }
      }

      console.error('Login failed: Invalid response');
      setIsLoading(false);
      return false;
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setIsLoading(false);
      throw error; // Re-throw to show error in UI
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {
        // Call Cognito logout endpoint
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      setIsLoading(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions.includes(permission) || user?.permissions.includes('all') || false;
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const accessToken = localStorage.getItem('accessToken');

    if (savedUser && accessToken) {
      // Verify token is still valid
      axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })
      .then(() => {
        setUser(JSON.parse(savedUser));
      })
      .catch(() => {
        // Token invalid, clear session
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
      });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

